import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Activity, Loader2, Plus, X, Save, Send, Eye, Clock, Mail, CheckCircle2,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { formatDateTime } from "../../lib/date";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HeartbeatSettings() {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [recipients, setRecipients] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [lastSent, setLastSent] = useState(null);
  const [lastMode, setLastMode] = useState(null);
  const [sender, setSender] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewMode, setPreviewMode] = useState("daily");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [testTo, setTestTo] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/heartbeat/settings`);
      setEnabled(r.data.enabled !== false);
      setRecipients(r.data.recipients || []);
      setLastSent(r.data.last_sent_at || null);
      setLastMode(r.data.last_mode || null);
      setSender(r.data.sender || "");
    } catch { toast.error("Failed to load Heartbeat settings"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addEmail = () => {
    const e = newEmail.trim();
    if (!e || !e.includes("@")) { toast.error("Enter a valid email"); return; }
    if (recipients.includes(e)) { toast.error("Already added"); return; }
    setRecipients([...recipients, e]);
    setNewEmail("");
  };

  const removeEmail = (e) => setRecipients(recipients.filter(r => r !== e));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/heartbeat/settings`, { enabled, recipients });
      toast.success("Heartbeat settings saved");
      await load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const loadPreview = async (mode) => {
    setPreviewMode(mode);
    setPreviewLoading(true);
    try {
      const r = await axios.get(`${API}/heartbeat/preview?mode=${mode}`);
      setPreviewHtml(r.data.html);
    } catch { toast.error("Preview failed"); }
    finally { setPreviewLoading(false); }
  };

  const sendTest = async (mode) => {
    setSending(true);
    try {
      const body = { mode };
      if (testTo.trim()) body.to = testTo.trim();
      const r = await axios.post(`${API}/heartbeat/send-test`, body);
      if (r.data.sent > 0) toast.success(`Test ${mode} Heartbeat sent to ${r.data.sent} recipient(s)`);
      else toast.warning(`Built ${r.data.sections} section(s) but 0 emails sent — check the email domain is verified in production`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  if (loading) return <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#8A857A]" /></div>;

  return (
    <div className="space-y-5" data-testid="heartbeat-settings">
      <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#FFF8D6] flex items-center justify-center shrink-0">
                <Activity className="w-5 h-5 text-[#8A6D00]" />
              </div>
              <div>
                <h3 className="text-[#1A1A1A] text-lg font-semibold">EQUIPR Heartbeat</h3>
                <p className="text-sm text-[#59554D] max-w-xl">One leadership digest rolling up Support, Sales, Customer Success and Competitors — instead of separate emails. Sent daily at <b className="text-[#1A1A1A]">08:00 AEST</b>, with a richer edition every <b className="text-[#1A1A1A]">Monday</b>. Empty sections are skipped.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs text-[#8A857A]">{enabled ? "On" : "Off"}</span>
              <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="heartbeat-enabled-switch" />
            </div>
          </div>

          {lastSent && (
            <div className="flex items-center gap-2 text-xs text-[#8A857A]">
              <CheckCircle2 className="w-3.5 h-3.5 text-[#0E6B3F]" /> Last sent {formatDateTime(lastSent)} ({lastMode || "daily"})
            </div>
          )}
          {sender && (
            <div className="flex items-center gap-2 text-xs text-[#8A857A]" data-testid="heartbeat-sender">
              <Send className="w-3.5 h-3.5 text-[#8A857A]" /> Sent from <span className="text-[#59554D]">{sender}</span>
            </div>
          )}

          {/* Recipients */}
          <div>
            <div className="text-xs uppercase tracking-wider text-[#8A857A] mb-2 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Recipients</div>
            <div className="flex flex-wrap gap-2 mb-3">
              {recipients.length === 0 && <span className="text-sm text-[#8A857A]">No recipients yet — add leadership emails below.</span>}
              {recipients.map(e => (
                <Badge key={e} className="bg-[#F4F2ED] border border-[#DAD5CA] text-[#59554D] gap-1.5 py-1 pl-2.5 pr-1.5" data-testid={`heartbeat-recipient-${e}`}>
                  {e}
                  <button onClick={() => removeEmail(e)} className="hover:text-[#C23A00]" data-testid={`heartbeat-remove-${e}`}><X className="w-3 h-3" /></button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2 max-w-md">
              <Input value={newEmail} onChange={e => setNewEmail(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
                placeholder="name@equipr.software"
                className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A]" data-testid="heartbeat-email-input" />
              <Button onClick={addEmail} variant="outline" className="border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#F4F2ED] shrink-0" data-testid="heartbeat-add-email">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <Button onClick={save} disabled={saving} className="bg-[#FFD108] hover:bg-[#FFF8D6] text-[#1A1A1A]" data-testid="heartbeat-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Save
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview + test */}
      <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h4 className="text-[#1A1A1A] font-medium flex items-center gap-2"><Eye className="w-4 h-4 text-[#1D4ED8]" /> Preview &amp; test</h4>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => loadPreview("daily")} className="border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#F4F2ED]" data-testid="heartbeat-preview-daily">Preview daily</Button>
              <Button size="sm" variant="outline" onClick={() => loadPreview("weekly")} className="border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#F4F2ED]" data-testid="heartbeat-preview-weekly">Preview weekly</Button>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Input value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="Send test to (optional — defaults to recipients)"
              className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-xs" data-testid="heartbeat-test-to" />
            <Button size="sm" onClick={() => sendTest("daily")} disabled={sending} className="bg-[#E3F3E9] text-[#0E6B3F] hover:bg-[#E3F3E9] border-0" data-testid="heartbeat-send-daily">
              {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Send test daily
            </Button>
            <Button size="sm" onClick={() => sendTest("weekly")} disabled={sending} className="bg-[#EFF6FF] text-[#8B89FF] hover:bg-[#EFF6FF] border-0" data-testid="heartbeat-send-weekly">
              <Send className="w-4 h-4 mr-1" /> Send test weekly
            </Button>
          </div>

          <div className="rounded-lg border border-[#DAD5CA] bg-white overflow-hidden" style={{ minHeight: 120 }}>
            {previewLoading ? (
              <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#999]" /></div>
            ) : previewHtml ? (
              <iframe title="heartbeat-preview" srcDoc={previewHtml} className="w-full" style={{ height: 560, border: 0 }} data-testid="heartbeat-preview-frame" />
            ) : (
              <div className="p-8 text-center text-[#999] text-sm flex items-center justify-center gap-2"><Clock className="w-4 h-4" /> Click "Preview daily" or "Preview weekly" to see the current Heartbeat.</div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
